import type { ActResult, ErrorCode, RoomAction, RoomView } from "@shared/types";
import { ApiError, api } from "./api";
import { asset } from "./asset";
import type { Seat } from "./prefs";
import { Emitter, type RoomClient, type RoomStatus } from "./room-client";

/** Online room: server-authoritative state over Server-Sent Events, actions over POST. */
export class NetRoom implements RoomClient {
  readonly local = false;
  private v: RoomView | null = null;
  private st: RoomStatus = "connecting";
  private reason: string | null = null;
  private es: EventSource | null = null;
  private em = new Emitter();
  private samples: number[] = [];
  private off = 0;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private errors = 0;
  private closed = false;
  private onVis = () => {
    if (document.visibilityState === "visible" && !this.closed && this.st !== "gone") {
      if (!this.es || this.es.readyState === EventSource.CLOSED) this.open();
      void this.pollOnce();
    }
  };

  constructor(readonly code: string, private seat: Seat | null) {
    this.open();
    document.addEventListener("visibilitychange", this.onVis);
  }

  view() {
    return this.v;
  }
  status() {
    return this.st;
  }
  goneReason() {
    return this.reason;
  }
  offset() {
    return this.off;
  }
  subscribe(fn: () => void) {
    return this.em.subscribe(fn);
  }
  seatAuth() {
    return this.seat ? { playerId: this.seat.playerId, token: this.seat.token } : null;
  }

  private qs(): string {
    if (!this.seat) return "";
    return `?pid=${encodeURIComponent(this.seat.playerId)}&tok=${encodeURIComponent(this.seat.token)}`;
  }

  private open() {
    this.es?.close();
    const es = new EventSource(asset(`api/rooms/${this.code}/events${this.qs()}`));
    this.es = es;
    es.addEventListener("state", (e) => {
      this.errors = 0;
      this.stopPolling();
      try {
        this.accept(JSON.parse((e as MessageEvent).data));
      } catch (err) {
        console.error("[room] bad state", err);
      }
    });
    es.addEventListener("gone", (e) => {
      let reason = "gone";
      try {
        reason = JSON.parse((e as MessageEvent).data).reason ?? reason;
      } catch {
        /* ignore */
      }
      this.markGone(reason);
    });
    es.onerror = () => {
      if (this.closed || this.st === "gone") return;
      this.errors += 1;
      if (this.st === "live") this.setStatus("reconnecting");
      if (this.errors >= 2) this.startPolling();
    };
    this.armWatchdog();
  }

  private armWatchdog() {
    if (this.watchdog) clearTimeout(this.watchdog);
    this.watchdog = setTimeout(() => {
      if (!this.v && !this.closed) this.startPolling();
    }, 6000);
  }

  private startPolling() {
    if (this.pollTimer || this.closed) return;
    void this.pollOnce();
    this.pollTimer = setInterval(() => void this.pollOnce(), 2000);
  }

  private stopPolling() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  private async pollOnce() {
    try {
      const r = await api<{ ok: true; view: RoomView }>(`/rooms/${this.code}/state${this.qs()}`);
      this.accept(r.view);
      // SSE probably blocked by a proxy: keep polling, but try SSE again now and then
      if (this.es?.readyState === EventSource.CLOSED) this.open();
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) this.markGone("not_found");
    }
  }

  private accept(view: RoomView) {
    if (this.v && view.version < this.v.version) return; // stale
    const sample = view.serverNow - Date.now();
    this.samples.push(sample);
    if (this.samples.length > 12) this.samples.shift();
    this.off = Math.max(...this.samples); // smallest latency wins
    // a seat that the server no longer knows (kicked / expired) shows up as me=null
    if (this.seat && view.me === null && this.v?.me) {
      this.v = view;
      this.markGone("kicked");
      return;
    }
    this.v = view;
    this.setStatus("live");
    this.em.emit();
  }

  private setStatus(s: RoomStatus) {
    if (this.st === s) return;
    this.st = s;
    this.em.emit();
  }

  private markGone(reason: string) {
    this.reason = reason;
    this.st = "gone";
    this.stopPolling();
    this.es?.close();
    this.em.emit();
  }

  async act(action: RoomAction): Promise<ActResult> {
    if (!this.seat) return { ok: false, error: "not_player" };
    try {
      await api(`/rooms/${this.code}/act`, {
        method: "POST",
        body: { playerId: this.seat.playerId, action },
        headers: { "x-player-token": this.seat.token },
        timeoutMs: 10_000,
      });
      return { ok: true };
    } catch (e) {
      const code = (e instanceof ApiError ? e.code : "server") as ErrorCode;
      if (code === ("kicked" as ErrorCode)) this.markGone("kicked");
      return { ok: false, error: code };
    }
  }

  close() {
    this.closed = true;
    this.stopPolling();
    if (this.watchdog) clearTimeout(this.watchdog);
    this.es?.close();
    document.removeEventListener("visibilitychange", this.onVis);
  }
}
